import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Grid, Divider, TextField, InputAdornment, IconButton, Tabs, Tab, Avatar,
  Stepper, Step, StepLabel, StepContent, Alert
} from '@mui/material';
import {
  Visibility, CheckCircle, AccountBalance, CreditCard, QrCode2,
  PaymentsOutlined, Search, Refresh, TrendingUp, AdminPanelSettings,
  AttachMoney, Person, LocalShipping
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const ORDER_FLOW = [
  { status: 'pending',               label: 'Order Received',          icon: '📋' },
  { status: 'confirmed',             label: 'Order Confirmed',          icon: '✅' },
  { status: 'processing',            label: 'Processing',               icon: '⚙️' },
  { status: 'manufacturing',         label: 'Manufacturing',            icon: '🏭' },
  { status: 'quality_check',         label: 'Quality Check',            icon: '🔍' },
  { status: 'ready_to_ship',         label: 'Ready to Ship',            icon: '📦' },
  { status: 'in_transit_domestic',   label: 'In Transit (Domestic)',    icon: '🚛' },
  { status: 'at_warehouse',          label: 'At OSESG Warehouse',       icon: '🏢' },
  { status: 'shipped_international', label: 'Shipped International',    icon: '✈️' },
  { status: 'customs_clearance',     label: 'Customs Clearance',        icon: '🛃' },
  { status: 'delivered',             label: 'Delivered',                icon: '🎉' },
  { status: 'installed',             label: 'Installed',                icon: '🔧' },
  { status: 'completed',             label: 'Completed',                icon: '🏆' },
  { status: 'cancelled',             label: 'Cancelled',                icon: '❌' },
  { status: 'disputed',              label: 'Disputed',                 icon: '⚠️' },
];

const ALL_STATUSES = ORDER_FLOW.map(s => s.status);

const statusColors = {
  pending: 'warning', confirmed: 'info', processing: 'primary', manufacturing: 'secondary',
  quality_check: 'default', ready_to_ship: 'success', in_transit_domestic: 'primary',
  at_warehouse: 'default', shipped_international: 'secondary', customs_clearance: 'warning',
  delivered: 'success', installed: 'success', completed: 'success',
  cancelled: 'error', disputed: 'error'
};

const formatMoney = (amount, currency = 'USD') => {
  if (amount === undefined || amount === null || amount === '') return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch (_) {
    return `${currency} ${Number(amount).toLocaleString()}`;
  }
};

const paymentIcons = {
  bank_transfer: <AccountBalance sx={{ fontSize: 16 }} />,
  bank_card:     <CreditCard sx={{ fontSize: 16 }} />,
  paypal:        <PaymentsOutlined sx={{ fontSize: 16 }} />,
  alipay:        <QrCode2 sx={{ fontSize: 16 }} />,
  wechat_pay:    <QrCode2 sx={{ fontSize: 16 }} />
};

const AdminOrders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [payFilter, setPayFilter] = useState('');
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payRef, setPayRef] = useState('');
  const [search, setSearch] = useState('');
  const [tabVal, setTabVal] = useState(0);

  useEffect(() => { loadOrders(); }, [statusFilter, payFilter]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      const url = `/orders${params.length ? '?' + params.join('&') : ''}`;
      const { data } = await api.get(url);
      setOrders(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openDetail = async (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setPayAmount('');
    setPayRef('');
    setDetailDialog(true);
    try {
      const { data } = await api.get(`/orders/${order._id}`);
      setSelectedOrder(data.data);
      setNewStatus(data.data.status);
    } catch {}
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrder || !newStatus) return;
    setUpdating(true);
    try {
      await api.put(`/orders/${selectedOrder._id}/status`, {
        status: newStatus, note: statusNote || `Status updated by admin to ${newStatus}`
      });
      setStatusNote('');
      toast.success('Order status updated');
      await loadOrders();
      const { data } = await api.get(`/orders/${selectedOrder._id}`);
      setSelectedOrder(data.data);
      setNewStatus(data.data.status);
    } catch (err) { toast.error('Update failed'); }
    setUpdating(false);
  };

  const handlePaymentUpdate = async () => {
    if (!selectedOrder || !payAmount || parseFloat(payAmount) <= 0) return;
    setUpdating(true);
    try {
      await api.put(`/orders/${selectedOrder._id}/payment`, {
        amount: parseFloat(payAmount), reference: payRef
      });
      setPayAmount('');
      setPayRef('');
      toast.success('Payment recorded');
      await loadOrders();
      const { data } = await api.get(`/orders/${selectedOrder._id}`);
      setSelectedOrder(data.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Update failed'); }
    setUpdating(false);
  };

  const handleMarkRefunded = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      await api.put(`/orders/${selectedOrder._id}/payment`, {
        paymentStatus: 'refunded', reference: payRef
      });
      setPayRef('');
      toast.success('Order marked as refunded');
      await loadOrders();
      const { data } = await api.get(`/orders/${selectedOrder._id}`);
      setSelectedOrder(data.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Update failed'); }
    setUpdating(false);
  };

  const currentStepIndex = (order) => ORDER_FLOW.findIndex(s => s.status === order?.status);

  const filtered = orders.filter(o => {
    const ms = !search || o.orderNumber?.toLowerCase().includes(search.toLowerCase()) ||
      o.customer?.firstName?.toLowerCase().includes(search.toLowerCase());
    const mp = !payFilter || o.paymentStatus === payFilter;
    if (tabVal === 1) return ms && mp && o.paymentStatus === 'unpaid';
    if (tabVal === 2) return ms && mp && ['delivered', 'completed'].includes(o.status);
    if (tabVal === 3) return ms && mp && ['cancelled', 'disputed'].includes(o.status);
    return ms && mp;
  });

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('manage_orders')}</Typography>
          <Typography variant="body2" color="text.secondary">Full visibility and control over all orders</Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <TextField size="small" placeholder="Search order / customer..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Order Status</InputLabel>
            <Select value={statusFilter} label="Order Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All Statuses</MenuItem>
              {ALL_STATUSES.map(s => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Payment</InputLabel>
            <Select value={payFilter} label="Payment" onChange={(e) => setPayFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['unpaid', 'partial', 'paid', 'refunded'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <IconButton onClick={loadOrders}><Refresh /></IconButton>
        </Box>
      </Box>

      {/* Stats */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        {[
          { label: 'Total',    count: orders.length,                                                        color: 'default' },
          { label: 'Pending',  count: orders.filter(o => o.status === 'pending').length,                   color: 'warning' },
          { label: 'Unpaid',   count: orders.filter(o => o.paymentStatus === 'unpaid').length,             color: 'error'   },
          { label: 'Delivered',count: orders.filter(o => ['delivered','completed'].includes(o.status)).length, color: 'success' },
        ].map(s => (
          <Chip key={s.label} icon={<TrendingUp sx={{ fontSize: 16 }} />}
            label={`${s.label}: ${s.count}`} color={s.color} variant="outlined" />
        ))}
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabVal} onChange={(_, v) => setTabVal(v)}>
          <Tab label="All Orders" />
          <Tab label="Unpaid" />
          <Tab label="Completed" />
          <Tab label="Issues" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Order #</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Supplier</strong></TableCell>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell><strong>Commission</strong></TableCell>
              <TableCell><strong>Payment</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((order) => (
              <TableRow key={order._id} hover
                sx={{ bgcolor: order.paymentStatus === 'unpaid' && order.status !== 'cancelled' ? '#fff8e1' : 'inherit' }}>
                <TableCell><strong>{order.orderNumber}</strong></TableCell>
                <TableCell>
                  <Typography variant="body2">{order.customer?.firstName} {order.customer?.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">{order.customer?.customerId}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{order.supplier?.companyName || 'N/A'}</Typography>
                </TableCell>
                <TableCell><strong>${(order.pricing?.total || 0).toLocaleString()}</strong></TableCell>
                <TableCell>${(order.pricing?.commission || 0).toLocaleString()}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {paymentIcons[order.paymentMethod] || <AccountBalance sx={{ fontSize: 16 }} />}
                    <Typography variant="caption">{(order.paymentMethod || 'N/A').replace(/_/g, ' ')}</Typography>
                  </Box>
                  <Chip label={order.paymentStatus || 'unpaid'} size="small"
                    color={order.paymentStatus === 'paid' ? 'success' : order.paymentStatus === 'partial' ? 'warning' : 'error'}
                    sx={{ mt: 0.5 }} />
                </TableCell>
                <TableCell>
                  <Chip label={order.status?.replace(/_/g, ' ')} size="small"
                    color={statusColors[order.status] || 'default'} />
                </TableCell>
                <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button size="small" variant="contained" startIcon={<Visibility />}
                    onClick={() => openDetail(order)}>
                    Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>No orders found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Admin Detail & Control Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminPanelSettings /> Admin Control: {selectedOrder?.orderNumber}
          <Box sx={{ flex: 1 }} />
          <Chip label={selectedOrder?.status?.replace(/_/g, ' ')} size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          <Chip label={selectedOrder?.paymentStatus} size="small"
            color={selectedOrder?.paymentStatus === 'paid' ? 'success' : 'warning'} />
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* Col 1: Info */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person fontSize="small" /> Customer Details
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2"><strong>{selectedOrder.customer?.firstName} {selectedOrder.customer?.lastName}</strong></Typography>
                  <Typography variant="body2" color="text.secondary">{selectedOrder.customer?.email}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedOrder.customer?.phone}</Typography>
                  <Typography variant="caption" color="primary">ID: {selectedOrder.customer?.customerId}</Typography>
                </Paper>

                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShipping fontSize="small" /> Delivery Address
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2">{selectedOrder.shippingAddress?.contactName}</Typography>
                  <Typography variant="body2">{selectedOrder.shippingAddress?.address}</Typography>
                  <Typography variant="body2">{selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.country}</Typography>
                  <Typography variant="body2">{selectedOrder.shippingAddress?.phone}</Typography>
                </Paper>

                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney fontSize="small" /> Financials
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                  {[
                    ['Subtotal',    selectedOrder.pricing?.subtotal],
                    ['Shipping',    selectedOrder.pricing?.shippingCost],
                    ['Customs',     selectedOrder.pricing?.customsCost],
                    ['Commission',  selectedOrder.pricing?.commission, `(${selectedOrder.pricing?.commissionRate}%)`],
                    ['Tax',         selectedOrder.pricing?.tax],
                    ['TOTAL',       selectedOrder.pricing?.total],
                  ].map(([label, val, suffix]) => (
                    <Box key={label} display="flex" justifyContent="space-between" py={0.25}>
                      <Typography variant="body2" color={label === 'TOTAL' ? 'secondary.main' : 'text.secondary'} fontWeight={label === 'TOTAL' ? 700 : 400}>
                        {label} {suffix}
                      </Typography>
                      <Typography variant="body2" fontWeight={label === 'TOTAL' ? 800 : 400}
                        color={label === 'TOTAL' ? 'secondary.main' : 'text.primary'}>
                        ${(val || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Paper>

                {/* Items */}
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Order Items</Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  {selectedOrder.items?.map((item, i) => (
                    <Box key={i} sx={{ px: 2, py: 1, borderBottom: i < selectedOrder.items.length - 1 ? '1px solid #eee' : 'none' }}>
                      <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">{item.osesgCode} · ×{item.quantity}</Typography>
                        <Typography variant="caption" fontWeight={700}>${item.totalPrice?.toLocaleString()}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              </Grid>

              {/* Col 2: Process Timeline */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Order Process Timeline</Typography>
                <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 1 }}>
                  <Stepper orientation="vertical" activeStep={currentStepIndex(selectedOrder)}>
                    {ORDER_FLOW.map((step, index) => {
                      const current = currentStepIndex(selectedOrder);
                      const done = index < current;
                      const active = index === current;
                      return (
                        <Step key={step.status} completed={done}>
                          <StepLabel StepIconComponent={() => (
                            <Avatar sx={{
                              width: 26, height: 26, fontSize: '0.7rem',
                              bgcolor: done ? 'success.main' : active ? 'primary.main' : '#e0e0e0',
                              color: done || active ? 'white' : 'text.disabled'
                            }}>
                              {done ? '✓' : step.icon}
                            </Avatar>
                          )}>
                            <Typography variant="body2" fontWeight={active || done ? 700 : 400}
                              color={active ? 'primary.main' : done ? 'success.main' : 'text.secondary'}>
                              {step.label}
                            </Typography>
                          </StepLabel>
                          <StepContent>
                            {selectedOrder.statusHistory?.filter(h => h.status === step.status).map((h, i) => (
                              <Typography key={i} variant="caption" display="block" color="text.secondary">
                                {new Date(h.timestamp).toLocaleString()} {h.note && `— ${h.note}`}
                              </Typography>
                            ))}
                          </StepContent>
                        </Step>
                      );
                    })}
                  </Stepper>
                </Box>
              </Grid>

              {/* Col 3: Admin Controls */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '2px solid #1a237e' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary">
                    🔧 Update Order Status
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>New Status</InputLabel>
                    <Select value={newStatus} label="New Status" onChange={(e) => setNewStatus(e.target.value)}>
                      {ALL_STATUSES.map(s => (
                        <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField fullWidth size="small" label="Status Note" value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)} sx={{ mb: 1.5 }} />
                  <Button fullWidth variant="contained" disabled={updating || newStatus === selectedOrder.status}
                    onClick={handleStatusUpdate}>
                    {updating ? <CircularProgress size={16} /> : 'Apply Status Update'}
                  </Button>
                </Paper>

                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '2px solid #2E7D32' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom color="success.dark">
                    💳 Confirm Payment
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    {paymentIcons[selectedOrder.paymentMethod]}
                    <Typography variant="body2" textTransform="capitalize">
                      {selectedOrder.paymentMethod?.replace(/_/g, ' ')} — {selectedOrder.paymentStatus}
                    </Typography>
                  </Box>

                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" display="block">Order Total</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatMoney(selectedOrder.pricing?.total, selectedOrder.pricing?.currency)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" display="block">Received</Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {formatMoney(selectedOrder.paymentDetails?.amountPaid || 0, selectedOrder.pricing?.currency)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" display="block">Remaining</Typography>
                      <Typography variant="body2" fontWeight={700} color={selectedOrder.remainingBalance > 0 ? 'error.main' : 'text.secondary'}>
                        {formatMoney(selectedOrder.remainingBalance ?? selectedOrder.pricing?.total, selectedOrder.pricing?.currency)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {selectedOrder.paymentDetails?.reference && (
                    <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
                      Ref: {selectedOrder.paymentDetails.reference}
                    </Alert>
                  )}
                  {selectedOrder.paymentDetails?.transactionId && (
                    <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
                      TxID: {selectedOrder.paymentDetails.transactionId}
                    </Alert>
                  )}

                  {selectedOrder.paymentStatus === 'paid' || selectedOrder.paymentStatus === 'refunded' ? (
                    <Alert severity={selectedOrder.paymentStatus === 'paid' ? 'success' : 'default'}>
                      This order is {selectedOrder.paymentStatus}. No further action needed.
                    </Alert>
                  ) : (
                    <>
                      <TextField
                        fullWidth size="small" type="number" label="Amount Received Now"
                        value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                        sx={{ mb: 1.5 }}
                        helperText="Verified against the order total — full amount marks it Paid, less marks it Partial."
                        InputProps={{ startAdornment: <InputAdornment position="start">{selectedOrder.pricing?.currency || '$'}</InputAdornment> }}
                      />
                      <TextField fullWidth size="small" label="Confirmation Reference" value={payRef}
                        onChange={(e) => setPayRef(e.target.value)} sx={{ mb: 1.5 }} />
                      <Button fullWidth variant="contained" color="success" sx={{ mb: 1 }}
                        disabled={updating || !payAmount || parseFloat(payAmount) <= 0}
                        onClick={handlePaymentUpdate}>
                        {updating ? <CircularProgress size={16} /> : 'Confirm Payment Received'}
                      </Button>
                      <Button fullWidth variant="outlined" color="error" size="small" disabled={updating}
                        onClick={handleMarkRefunded}>
                        Mark as Refunded
                      </Button>
                    </>
                  )}
                </Paper>

                {/* Status History */}
                {selectedOrder.statusHistory?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Status Log</Typography>
                    <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                      {[...selectedOrder.statusHistory].reverse().map((h, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1, pb: 1, borderBottom: '1px solid #f0f0f0' }}>
                          <CheckCircle sx={{ fontSize: 14, color: 'success.main', mt: 0.3, flexShrink: 0 }} />
                          <Box>
                            <Typography variant="caption" fontWeight={600} textTransform="capitalize">
                              {h.status?.replace(/_/g, ' ')}
                            </Typography>
                            {h.note && <Typography variant="caption" display="block" color="text.secondary">{h.note}</Typography>}
                            <Typography variant="caption" color="text.disabled">
                              {new Date(h.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminOrders;
